import { useEffect } from 'react';
import { useStore } from '@/state/store';

function isTypingTarget(el: EventTarget | null) {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || node.isContentEditable;
}

export function useKeyboardShortcuts() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const s = useStore.getState();
      const mod = e.metaKey || e.ctrlKey;

      if (isTypingTarget(e.target)) return;

      /* ── Shared by every tool ──────────────────────────────────────── */

      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        s.selectAll();
        return;
      }
      if (mod && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        s.setUi({ exportOpen: true });
        return;
      }
      if (e.key === 'Escape') {
        s.select(null);
        return;
      }
      if (e.key === '?') {
        s.setUi({ shortcutsOpen: !s.ui.shortcutsOpen });
        return;
      }

      /* ── Per tool ──────────────────────────────────────────────────── */

      if (s.tool === 'collage') return collageKeys(e, s, mod);
      if (s.tool === 'carousel') return carouselKeys(e, s, mod);
      return splitterKeys(e, s);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}

type State = ReturnType<typeof useStore.getState>;

function collageKeys(e: KeyboardEvent, s: State, mod: boolean) {
  if (mod && e.key.toLowerCase() === 'm') {
    e.preventDefault();
    s.mergeSelection();
    return;
  }
  if (mod && e.key.toLowerCase() === 'd') {
    e.preventDefault();
    s.splitSelected(e.shiftKey ? 'horizontal' : 'vertical');
    return;
  }
  if ((e.key === 'Backspace' || e.key === 'Delete') && s.ui.selection.length) {
    e.preventDefault();
    s.ui.selection.forEach((id) => s.clearCell(id));
    return;
  }

  const single = s.ui.selection.length === 1 ? s.ui.selection[0] : null;
  if (!single) return;
  const slot = s.project.slots[single];
  if (!slot?.assetId) return;

  const nudge = e.shiftKey ? 0.04 : 0.008;
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    s.patchTransform(single, { x: slot.transform.x - nudge }, `nudge-${single}`);
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    s.patchTransform(single, { x: slot.transform.x + nudge }, `nudge-${single}`);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    s.patchTransform(single, { y: slot.transform.y - nudge }, `nudge-${single}`);
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    s.patchTransform(single, { y: slot.transform.y + nudge }, `nudge-${single}`);
  } else if (e.key === 'r' || e.key === 'R') {
    s.patchTransform(single, {
      rotation: (slot.transform.rotation + (e.shiftKey ? -90 : 90) + 360) % 360,
    });
  } else if (e.key === '0') {
    s.resetSlotTransform(single);
  }
}

function carouselKeys(e: KeyboardEvent, s: State, mod: boolean) {
  if (e.key === 't' || e.key === 'T') {
    s.addTextLayer();
    return;
  }
  const ids = s.ui.selection;
  if (!ids.length) return;

  if (mod && e.key.toLowerCase() === 'd') {
    e.preventDefault();
    s.duplicateLayers(ids);
    return;
  }
  if (e.key === 'Backspace' || e.key === 'Delete') {
    e.preventDefault();
    s.removeLayers(ids);
    return;
  }
  if (e.key === 'f' || e.key === 'F') {
    s.fillStrip(ids);
    return;
  }
  if (e.key === ']') {
    ids.forEach((id) => s.reorderLayer(id, e.altKey ? 'front' : 'up'));
    return;
  }
  if (e.key === '[') {
    ids.forEach((id) => s.reorderLayer(id, e.altKey ? 'back' : 'down'));
    return;
  }

  // Nudge in strip pixels, so a tap is a tap whatever the zoom.
  const step = e.shiftKey ? 10 : 1;
  const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[
    e.key
  ];
  if (!move) return;
  e.preventDefault();
  s.updateLayers(
    ids,
    (l) => {
      l.x += move[0];
      l.y += move[1];
    },
    'nudge-layer',
  );
}

function splitterKeys(e: KeyboardEvent, s: State) {
  const t = s.splitter.source.transform;
  if (!s.splitter.source.assetId) return;

  const nudge = e.shiftKey ? 0.04 : 0.008;
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    s.patchSourceTransform({ x: t.x - nudge }, 'nudge-source');
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    s.patchSourceTransform({ x: t.x + nudge }, 'nudge-source');
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    s.patchSourceTransform({ y: t.y - nudge }, 'nudge-source');
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    s.patchSourceTransform({ y: t.y + nudge }, 'nudge-source');
  } else if (e.key === 'r' || e.key === 'R') {
    s.patchSourceTransform({ rotation: (t.rotation + (e.shiftKey ? -90 : 90) + 360) % 360 });
  } else if (e.key === '0') {
    s.resetSource();
  }
}
